import { api } from "./api";
import type { Payable, Payment, SupplierBalance, ApplyCreditResult } from "@/types/models";

interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number };
}

export const payableService = {
  list: (params?: Record<string, string | number>, signal?: AbortSignal) =>
    api.get<PaginatedResponse<Payable>>("/payables", { params, signal }).then((r) => r.data),

  get: (id: number) =>
    api.get<Payable>(`/payables/${id}`).then((r) => r.data),

  create: (data: {
    supplier_id: number;
    date: string;
    due_date?: string;
    bill_number?: string;
    total_amount: number;
    notes?: string;
    attachment_ids?: number[];
  }) => api.post<Payable>("/payables", data).then((r) => r.data),

  getBalances: () =>
    api.get<SupplierBalance[]>("/payables/balances").then((r) => r.data),

  listPayments: (payableId: number) =>
    api.get<Payment[]>(`/payables/${payableId}/payments`).then((r) => r.data),

  // Record Payment: CASH only. Settles the supplier's open bills oldest-first
  // in one transaction; whatever is left is held as credit. Bank / UPI /
  // cheque payments come in through a statement upload instead.
  recordSupplierPayment: (data: { supplier_id: number; date: string; amount: number; notes?: string; local_id?: string }) =>
    api
      .post<{ payment: Payment; settled: ApplyCreditResult | null }>("/payables/supplier-payments", data)
      .then((r) => r.data),

  // FIFO-apply the supplier's available advance credit across all their open
  // bills in one action.
  applyCredit: (supplierID: number) =>
    api.post<ApplyCreditResult>("/payables/apply-credit", { supplier_id: supplierID }).then((r) => r.data),

  update: (id: number, data: {
    supplier_id: number;
    date: string;
    due_date?: string;
    bill_number?: string;
    total_amount: number;
    notes?: string;
  }) => api.put<Payable>(`/payables/${id}`, data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/payables/${id}`),

  updatePayment: (id: number, data: {
    payable_id: number;
    date: string;
    amount: number;
    method: string;
    reference?: string;
    notes?: string;
  }) => api.put<Payment>(`/payables/payments/${id}`, data).then((r) => r.data),

  deletePayment: (id: number) =>
    api.delete(`/payables/payments/${id}`),
};
